import * as React from "react";
import { useState } from "react";

import axios from "axios";

import {
  Button,
  CardActions,
  CircularProgress,
  Accordion, 
  AccordionDetails, 
  AccordionSummary,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  Box,
  Checkbox,
  FormControlLabel,
} from "@material-ui/core";
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { makeStyles } from "@material-ui/core/styles";

import { Notification, useNotify } from "react-admin";

import { ENDPOINTS } from "../apiConstants";
import { getConfigGet, getConfigPost } from "../getConfig";
import { ShowData } from "./transferRewardAmount";

const useStyles = makeStyles((theme) => ({
  main: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "flex-start",
    backgroundRepeat: "no-repeat",
    backgroundSize: "cover",
    padding: "0 20px",
    boxSizing: "border-box"
  },
  card: {
    minWidth: 300,
    marginTop: "6em",
  },
  actions: {
    padding: "0 1em 1em 1em",
  },
  summaryCard: {
    margin: '10px 0',
    padding: '15px',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    backgroundColor: '#f9f9f9'
  },
  errorTypeCard: {
    margin: '10px 0',
    padding: '15px',
    border: '1px solid #ff9800',
    borderRadius: '8px',
    backgroundColor: '#fff3e0'
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '15px',
    marginBottom: '20px'
  },
  statItem: {
    textAlign: 'center',
    padding: '10px',
    backgroundColor: '#f5f5f5',
    borderRadius: '8px'
  },
  errorInstance: {
    margin: '5px 0',
    padding: '8px',
    backgroundColor: '#fafafa',
    border: '1px solid #ddd',
    borderRadius: '4px'
  },
  fullWidthAccordion: {
    width: '100%',
    maxWidth: '100%',
    margin: '0 auto'
  },
  fullWidthTable: {
    width: '100%',
    tableLayout: 'fixed',
    minWidth: '800px'
  },
  errorDetailsContainer: {
    width: '100%',
    maxWidth: '100%',
    overflow: 'hidden',
    wordBreak: 'break-word',
    boxSizing: 'border-box'
  },
  tableCell: {
    padding: '8px 12px',
    border: '1px solid gray',
    wordBreak: 'break-word',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  selectColumn: {
    textAlign: 'center',
    width: '60px',
    minWidth: '60px',
    maxWidth: '60px'
  },
  dataColumn: {
    textAlign: 'left',
    wordBreak: 'break-word',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  sideBySideContainer: {
    display: 'flex',
    gap: '20px',
    width: '100%',
    marginBottom: '20px'
  },
  sideBySideItem: {
    flex: 1,
    minWidth: 0
  },
  grayedOutRow: {
    backgroundColor: '#f5f5f5',
    opacity: 0.6,
    color: '#999'
  },
  grayedOutCell: {
    backgroundColor: '#f5f5f5',
    opacity: 0.6,
    color: '#999',
    textDecoration: 'line-through'
  }
}));

const TrueLayerSyncFailureMonitor = () => {
  const classes = useStyles();
  const notify = useNotify();

  const [loading, setLoading] = useState(false);
  const [monitorData, setMonitorData] = useState<any>(null);
  const [expanded, setExpanded] = useState<string[]>(['queueStatistics', 'summary']);
  const [retrying, setRetrying] = useState<{ [key: string]: boolean }>({});
  const [selectedUsers, setSelectedUsers] = useState<{ [errorType: string]: Set<string> }>({});
  const [selectAll, setSelectAll] = useState<{ [errorType: string]: boolean }>({});
  const [grayedOutUsers, setGrayedOutUsers] = useState<{ [errorType: string]: Set<string> }>({});

  const handleFetchData = () => {
    setLoading(true);
    setMonitorData(null);
    // Clear gray-out state when refreshing to show fresh data
    setGrayedOutUsers({});
    notify("Fetching TrueLayer sync failure data...", "info");

    const config = getConfigGet({});

    axios.get(ENDPOINTS.TRUELAYER_SYNC_FAILURE_MONITOR, config)
      .then((res: any) => {
        setMonitorData(res?.data[0]);
        setLoading(false);
        notify("Successfully fetched TrueLayer sync failure data", "info", "", undefined, 5000);
      })
      .catch((error) => {
        setLoading(false);
        notify(error?.response?.data?.message || 'Failed to fetch TrueLayer sync failure data', "error", "", undefined, 10000);
      });
  };

  // Handle individual user selection
  const handleUserSelect = (errorType: string, userId: string, checked: boolean) => {
    setSelectedUsers(prev => {
      const newSelection = { ...prev };
      if (!newSelection[errorType]) {
        newSelection[errorType] = new Set();
      }
      
      if (checked) {
        newSelection[errorType].add(userId);
      } else {
        newSelection[errorType].delete(userId);
      }
      
      // Update select all state
      const totalUsers = monitorData?.errorTypeTables?.find((table: any) => table.errorType === errorType)?.users?.length || 0;
      setSelectAll(prev => ({
        ...prev,
        [errorType]: newSelection[errorType].size === totalUsers
      }));
      
      return newSelection;
    });
  };

  // Handle select all for an error type
  const handleSelectAll = (errorType: string, checked: boolean) => {
    const errorTypeTable = monitorData?.errorTypeTables?.find((table: any) => table.errorType === errorType);
    if (!errorTypeTable?.users) return;

    setSelectedUsers(prev => {
      const newSelection = { ...prev };
      if (checked) {
        newSelection[errorType] = new Set(errorTypeTable.users.map((user: any) => user.userId));
      } else {
        newSelection[errorType] = new Set();
      }
      return newSelection;
    });

    setSelectAll(prev => ({
      ...prev,
      [errorType]: checked
    }));
  };

  // Get selected users for an error type
  const getSelectedUsers = (errorType: string) => {
    const errorTypeTable = monitorData?.errorTypeTables?.find((table: any) => table.errorType === errorType);
    if (!errorTypeTable?.users) return [];

    const selectedUserIds = selectedUsers[errorType] || new Set();
    return errorTypeTable.users.filter((user: any) => selectedUserIds.has(user.userId));
  };

  // Get selected receipt handles for an error type
  const getSelectedReceiptHandles = (errorType: string) => {
    const errorTypeTable = monitorData?.errorTypeTables?.find((table: any) => table.errorType === errorType);
    if (!errorTypeTable?.users) return [];

    const selectedUserIds = selectedUsers[errorType] || new Set();
    return errorTypeTable.users
      .filter((user: any) => selectedUserIds.has(user.userId))
      .map((user: any) => user.receiptHandle);
  };

  // Check if a user is grayed out (successfully processed)
  const isUserGrayedOut = (errorType: string, userId: string) => {
    return grayedOutUsers[errorType]?.has(userId) || false;
  };

  // Gray out users after successful operation
  const grayOutUsers = (errorType: string, userIds: string[]) => {
    setGrayedOutUsers(prev => {
      const newGrayedOut = { ...prev };
      if (!newGrayedOut[errorType]) {
        newGrayedOut[errorType] = new Set();
      }
      userIds.forEach(userId => newGrayedOut[errorType].add(userId));
      return newGrayedOut;
    });
  };

  // Helper: Process large payloads in smaller batches to avoid size limits
  const processInBatches = async (
    errorType: string, 
    users: any[], 
    receiptHandles: string[], 
    config: any,
    action?: string
  ) => {
    const BATCH_SIZE = action === 'remove' ? 20 : 5;
    let totalSent = 0;
    let totalDeleted = 0;

    for (let i = 0; i < users.length; i += BATCH_SIZE) {
      const batchUsers = users.slice(i, i + BATCH_SIZE);
      const batchHandles = receiptHandles.slice(i, i + BATCH_SIZE);
      
      try {
        const data = {
          errorType,
          users: action === 'remove' ? [] : batchUsers,
          receiptHandles: batchHandles,
          action
        };

        const response = await axios.patch(ENDPOINTS.TRUELAYER_SYNC_FAILURE_MONITOR, data, { headers: config });
        totalSent += (response.data as any).sentToMainQueue || 0;
        totalDeleted += (response.data as any).deletedFromDLQ || 0;
        
      } catch (error) {
        // If batch fails and has multiple users, retry one by one
        if (batchUsers.length > 1) {
          console.log(`Batch failed, retrying ${batchUsers.length} users individually...`);
          
          for (let j = 0; j < batchUsers.length; j++) {
            try {
              const singleData = {
                errorType,
                users: action === 'remove' ? [] : [batchUsers[j]],
                receiptHandles: [batchHandles[j]],
                action
              };
              
              const singleResponse = await axios.patch(ENDPOINTS.TRUELAYER_SYNC_FAILURE_MONITOR, singleData, { headers: config });
              totalSent += (singleResponse.data as any).sentToMainQueue || 0;
              totalDeleted += (singleResponse.data as any).deletedFromDLQ || 0;
            } catch (individualError) {
              console.error(`User ${batchUsers[j]?.userId} failed:`, individualError);
            }
          }
        } else {
          // Single user batch failed - just log it
          console.error(`Failed to process user ${batchUsers[0]?.userId}:`, error);
        }
      }
    }

    return { sentToMainQueue: totalSent, deletedFromDLQ: totalDeleted };
  };

  const handleRetry = (errorType: string, users: any[], receiptHandles: string[]) => {
    setRetrying(prev => ({ ...prev, [errorType]: true }));
    notify(`Retrying ${users.length} users for ${errorType}...`, "info");

    const config = getConfigPost();

    processInBatches(errorType, users, receiptHandles, config)
      .then((response: any) => {
        setRetrying(prev => ({ ...prev, [errorType]: false }));
        notify(response.message || `Successfully retried ${users.length} users for ${errorType}`, "info", "", undefined, 5000);
        
        if (response.sentToMainQueue > 0) {
          const userIds = users.map(user => user.userId);
          grayOutUsers(errorType, userIds);
        }
        
        setSelectedUsers(prev => ({ ...prev, [errorType]: new Set() }));
        setSelectAll(prev => ({ ...prev, [errorType]: false }));
      })
      .catch((error) => {
        setRetrying(prev => ({ ...prev, [errorType]: false }));
        const errorMessage = error?.response?.data?.message || error?.message || `Failed to retry users for ${errorType}`;
        notify(errorMessage, "error", "", undefined, 10000);
      });
  };

  const handleRemoveFromDLQ = (errorType: string, users: any[], receiptHandles: string[]) => {
    setRetrying(prev => ({ ...prev, [`${errorType}_remove`]: true }));
    notify(`Removing ${users.length} users from DLQ for ${errorType}...`, "info");

    const config = getConfigPost();

    processInBatches(errorType, users, receiptHandles, config, 'remove')
      .then((response: any) => {
        setRetrying(prev => ({ ...prev, [`${errorType}_remove`]: false }));
        notify(response.message || `Successfully removed ${users.length} users from DLQ for ${errorType}`, "info", "", undefined, 5000);
        
        if (response.deletedFromDLQ > 0) {
          const userIds = users.map(user => user.userId);
          grayOutUsers(errorType, userIds);
        }
        
        setSelectedUsers(prev => ({ ...prev, [errorType]: new Set() }));
        setSelectAll(prev => ({ ...prev, [errorType]: false }));
      })
      .catch((error) => {
        setRetrying(prev => ({ ...prev, [`${errorType}_remove`]: false }));
        const errorMessage = error?.response?.data?.message || error?.message || `Failed to remove users from DLQ for ${errorType}`;
        notify(errorMessage, "error", "", undefined, 10000);
      });
  };

  // Handle accordion toggle
  const handleChange = (title: string) => (event: React.ChangeEvent<{}>, isExpanded: boolean) => {
    setExpanded((prev) => {
      if (isExpanded) {
        return [...prev, title];
      } else {
        return prev.filter((t) => t !== title);
      }
    });
  };

  const formatQueueStatistics = (stats: any) => {
    if (!stats) return [];
    
    return [
      ['Queue Statistics', ''],
      ['Available Messages', stats.approximateNumberOfMessages?.toString() || '0'],
      ['Messages Not Visible', stats.approximateNumberOfMessagesNotVisible?.toString() || '0'],
      ['Delayed Messages', stats.approximateNumberOfMessagesDelayed?.toString() || '0'],
      ['Visibility Timeout', `${stats.visibilityTimeoutSeconds || 30} seconds`],
      ['Last Updated', stats.lastUpdated || 'N/A']
    ];
  };

  const formatSummary = (summary: any) => {
    if (!summary) return [];
    
    return [
      ['Summary', ''],
      ['Total Errors', summary.totalErrors?.toString() || '0'],
      ['Unique Error Types', summary.uniqueErrorTypes?.toString() || '0'],
      ['Total Messages', summary.totalMessages?.toString() || '0']
    ];
  };



  return (
    <>
      <div className={classes.main}>
        <CardActions className={classes.actions}>
          <Button
            variant="contained"
            type="button"
            color='primary'
            disabled={loading}
            fullWidth
            onClick={handleFetchData}
          >
            {loading && <CircularProgress size={25} thickness={2} />}
            Fetch TrueLayer Sync Failure Data
          </Button>
        </CardActions>

        <Notification />

        {monitorData && (
          <>
            {/* Queue Statistics and Summary - Side by Side */}
            <div className={classes.sideBySideContainer}>
              {/* Queue Statistics */}
              <div className={classes.sideBySideItem}>
                <Accordion
                  expanded={expanded.includes('queueStatistics')}
                  onChange={handleChange('queueStatistics')}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6">Queue Statistics</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <ShowData 
                      record={formatQueueStatistics(monitorData.queueStatistics)} 
                      tableCenterAlign={false} 
                    />
                  </AccordionDetails>
                </Accordion>
              </div>

              {/* Summary */}
              {monitorData.summary && (
                <div className={classes.sideBySideItem}>
                  <Accordion
                    expanded={expanded.includes('summary')}
                    onChange={handleChange('summary')}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="h6">Summary</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <ShowData 
                        record={formatSummary(monitorData.summary)} 
                        tableCenterAlign={false} 
                      />
                    </AccordionDetails>
                  </Accordion>
                </div>
              )}
            </div>

            {/* Error Type Tables */}
            {monitorData.errorTypeTables && monitorData.errorTypeTables.map((errorTypeTable: any, index: number) => (
              <Accordion
                key={errorTypeTable.errorType}
                expanded={expanded.includes(`errorType_${errorTypeTable.errorType}`)}
                onChange={handleChange(`errorType_${errorTypeTable.errorType}`)}
                className={classes.fullWidthAccordion}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6">
                    {errorTypeTable.errorType} ({errorTypeTable.count} users)
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <div style={{ width: '100%' }}>
                    {/* Error Information Header */}
                    <div className={classes.errorDetailsContainer} style={{ 
                      marginBottom: '20px', 
                      padding: '15px',
                      border: '1px solid #e0e0e0',
                      borderRadius: '8px',
                      backgroundColor: '#f9f9f9'
                    }}>
                      <Typography variant="h6" gutterBottom>Error Details</Typography>
                      <div style={{ marginBottom: '15px' }}>
                        <Typography variant="body2" style={{ marginBottom: '5px' }}>
                          <strong>Error Message:</strong>
                        </Typography>
                        <Typography variant="body2" style={{ marginLeft: '10px', fontStyle: 'italic' }}>
                          {errorTypeTable.errorMessage}
                        </Typography>
                      </div>
                      <div>
                        <Typography variant="body2" style={{ marginBottom: '5px' }}>
                          <strong>Error Stack:</strong>
                        </Typography>
                        <Typography variant="body2" style={{ 
                          whiteSpace: 'pre-wrap', 
                          fontFamily: 'monospace', 
                          fontSize: '12px',
                          marginLeft: '10px',
                          backgroundColor: '#f5f5f5',
                          padding: '10px',
                          borderRadius: '4px',
                          border: '1px solid #ddd',
                          display: 'block',
                          overflow: 'auto',
                          maxHeight: '200px',
                          wordBreak: 'break-word'
                        }}>
                          {errorTypeTable.errorStack}
                        </Typography>
                      </div>
                    </div>
                    
                    {/* Selection and Action Buttons */}
                    <div style={{ marginBottom: '15px' }}>
                      {/* Select All Checkbox */}
                      <div style={{ marginBottom: '10px' }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={selectAll[errorTypeTable.errorType] || false}
                              onChange={(e) => handleSelectAll(errorTypeTable.errorType, e.target.checked)}
                              color="primary"
                            />
                          }
                          label={`Select All (${errorTypeTable.users?.length || 0} users)`}
                        />
                      </div>

                      {/* Action Buttons */}
                      <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                        <Button
                          variant="contained"
                          color="primary"
                          disabled={retrying[errorTypeTable.errorType] || getSelectedUsers(errorTypeTable.errorType).length === 0}
                          onClick={() => {
                            const selectedUsers = getSelectedUsers(errorTypeTable.errorType);
                            const selectedReceiptHandles = getSelectedReceiptHandles(errorTypeTable.errorType);
                            handleRetry(errorTypeTable.errorType, selectedUsers, selectedReceiptHandles);
                          }}
                          startIcon={retrying[errorTypeTable.errorType] ? <CircularProgress size={20} /> : null}
                        >
                          {retrying[errorTypeTable.errorType] ? 'Pushing...' : `Push Selected to SQS (${getSelectedUsers(errorTypeTable.errorType).length})`}
                        </Button>

                        <Button
                          variant="contained"
                          color="secondary"
                          disabled={retrying[`${errorTypeTable.errorType}_remove`] || getSelectedUsers(errorTypeTable.errorType).length === 0}
                          onClick={() => {
                            const selectedUsers = getSelectedUsers(errorTypeTable.errorType);
                            const selectedReceiptHandles = getSelectedReceiptHandles(errorTypeTable.errorType);
                            handleRemoveFromDLQ(errorTypeTable.errorType, selectedUsers, selectedReceiptHandles);
                          }}
                          startIcon={retrying[`${errorTypeTable.errorType}_remove`] ? <CircularProgress size={20} /> : null}
                        >
                          {retrying[`${errorTypeTable.errorType}_remove`] ? 'Removing...' : `Remove Selected from DLQ (${getSelectedUsers(errorTypeTable.errorType).length})`}
                        </Button>
                      </div>

                      {/* Info Text */}
                      {getSelectedUsers(errorTypeTable.errorType).length > 0 && (
                        <Typography variant="caption" display="block" style={{ marginTop: '5px', color: '#666', textAlign: 'right' }}>
                          {getSelectedUsers(errorTypeTable.errorType).length} user(s) selected
                        </Typography>
                      )}
                    </div>

                    {/* Data Table */}
                    <div style={{ width: '100%', overflowX: 'auto' }}>
                      <Table style={{ width: '100%', tableLayout: 'auto' }}>
                        <TableBody>
                          {/* Header Row */}
                          <TableRow>
                            <TableCell className={`${classes.tableCell} ${classes.selectColumn}`}>
                              Select
                            </TableCell>
                            {errorTypeTable.tableHeaders.map((header: string, index: number) => (
                              <TableCell 
                                key={index} 
                                className={`${classes.tableCell} ${classes.dataColumn}`}
                                style={{ width: `${100 / (errorTypeTable.tableHeaders.length + 1)}%` }}
                              >
                                {header}
                              </TableCell>
                            ))}
                          </TableRow>
                          
                          {/* Data Rows */}
                          {errorTypeTable.tableRows.map((row: any, rowIndex: number) => {
                            const userId = errorTypeTable.users?.[rowIndex]?.userId;
                            const isSelected = userId ? (selectedUsers[errorTypeTable.errorType] || new Set()).has(userId) : false;
                            const isGrayedOut = userId ? isUserGrayedOut(errorTypeTable.errorType, userId) : false;
                            
                            return (
                              <TableRow 
                                key={rowIndex}
                                className={isGrayedOut ? classes.grayedOutRow : ''}
                              >
                                <TableCell className={`${classes.tableCell} ${classes.selectColumn} ${isGrayedOut ? classes.grayedOutCell : ''}`}>
                                  <Checkbox
                                    checked={isSelected}
                                    onChange={(e) => handleUserSelect(errorTypeTable.errorType, userId, e.target.checked)}
                                    color="primary"
                                    disabled={isGrayedOut}
                                  />
                                </TableCell>
                                {row.map((cell: any, cellIndex: number) => (
                                  <TableCell 
                                    key={cellIndex} 
                                    className={`${classes.tableCell} ${classes.dataColumn} ${isGrayedOut ? classes.grayedOutCell : ''}`}
                                    style={{ width: `${100 / (errorTypeTable.tableHeaders.length + 1)}%` }}
                                  >
                                    {cell}
                                  </TableCell>
                                ))}
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                </AccordionDetails>
              </Accordion>
            ))}

            {/* Debug Info */}
            {monitorData.debugInfo && (
              <Accordion
                expanded={expanded.includes('debugInfo')}
                onChange={handleChange('debugInfo')}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6">Debug Information</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box className={classes.summaryCard}>
                    <Typography variant="h6" gutterBottom>Message Visibility</Typography>
                    <div className={classes.statsGrid}>
                      <div className={classes.statItem}>
                        <Typography variant="h4" color="primary">
                          {monitorData.debugInfo.visibleMessages}
                        </Typography>
                        <Typography variant="body2">Visible Messages</Typography>
                      </div>
                      <div className={classes.statItem}>
                        <Typography variant="h4" color="secondary">
                          {monitorData.debugInfo.invisibleMessages}
                        </Typography>
                        <Typography variant="body2">Invisible Messages</Typography>
                      </div>
                    </div>
                    <Typography variant="body2" color="textSecondary">
                      {monitorData.debugInfo.explanation?.whyCountsVary}
                    </Typography>
                  </Box>
                </AccordionDetails>
              </Accordion>
            )}

            {/* Timestamp */}
            <Box className={classes.summaryCard}>
              <Typography variant="body2" color="textSecondary">
                Last Updated: {monitorData.timestamp ? new Date(monitorData.timestamp).toLocaleString() : 'N/A'}
              </Typography>
            </Box>
          </>
        )}
      </div>
    </>
  );
};


export default TrueLayerSyncFailureMonitor;
